// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Trash2 } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, FormActions, Input, Select, Skeleton } from '@/components/ui'
import {
  useJournalEntry,
  useCreateJournalEntry,
  useUpdateJournalEntry,
  useGlAccounts,
} from '@/hooks/useAccounting'
import { useProperties } from '@/hooks/useProperties'
import { useLocalizationConfig } from '@/hooks/useDateFormatter'
import { localToday } from '@/lib/date'
import { formatBaht } from '@/lib/money'

const journalLineSchema = z.object({
  gl_account_id: z.string().min(1),
  debit: z.coerce.number().min(0).default(0),
  credit: z.coerce.number().min(0).default(0),
  description: z.string().optional(),
})

const journalEntryFormSchema = z.object({
  entry_date: z.string().min(1),
  description: z.string().min(1),
  property_id: z.string().optional(),
  journal_lines: z.array(journalLineSchema).min(2),
})

type JournalEntryFormData = z.infer<typeof journalEntryFormSchema>

export function JournalEntryFormPage() {
  const { t } = useTranslation('accounting')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const entryId = Number(id)
  const navigate = useNavigate()

  const { data: existingEntry, isLoading: isLoadingEntry } = useJournalEntry(
    isEditing ? entryId : 0
  )
  const createEntry = useCreateJournalEntry()
  const updateEntry = useUpdateJournalEntry()

  const { data: glAccountsData } = useGlAccounts({ active: true })
  const { data: propertiesData } = useProperties()
  const { timezone } = useLocalizationConfig()

  const glAccounts = glAccountsData?.data || []
  const properties = propertiesData?.data || []

  const glAccountOptions = [
    { value: '', label: t('placeholders.search_account') },
    ...glAccounts.map((a) => ({ value: String(a.id), label: `${a.code} - ${a.name}` })),
  ]

  const propertyOptions = [
    { value: '', label: '— ' + t('filters.all_properties') + ' —' },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<JournalEntryFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(journalEntryFormSchema) as any,
    defaultValues: {
      entry_date: localToday(timezone),
      description: '',
      property_id: '',
      journal_lines: [
        { gl_account_id: '', debit: 0, credit: 0, description: '' },
        { gl_account_id: '', debit: 0, credit: 0, description: '' },
      ],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'journal_lines',
  })

  useEffect(() => {
    if (isEditing && existingEntry) {
      if (existingEntry.status !== 'draft') {
        navigate(`/accounting/journal-entries/${entryId}`)
        return
      }
      reset({
        entry_date: existingEntry.entry_date,
        description: existingEntry.description,
        property_id: existingEntry.property_id ? String(existingEntry.property_id) : '',
        journal_lines: existingEntry.journal_lines.map((line) => ({
          gl_account_id: String(line.gl_account_id),
          debit: line.debit,
          credit: line.credit,
          description: line.description ?? '',
        })),
      })
    }
  }, [isEditing, existingEntry, reset, navigate, entryId])

  const watchedLines = watch('journal_lines')
  const totalDebit = watchedLines.reduce((sum, l) => sum + (Number(l.debit) || 0), 0)
  const totalCredit = watchedLines.reduce((sum, l) => sum + (Number(l.credit) || 0), 0)
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0

  const onSubmit = async (formData: JournalEntryFormData) => {
    const input = {
      entry_date: formData.entry_date,
      description: formData.description,
      property_id: formData.property_id ? Number(formData.property_id) : null,
      journal_lines: formData.journal_lines.map((l) => ({
        gl_account_id: Number(l.gl_account_id),
        debit: Number(l.debit) || 0,
        credit: Number(l.credit) || 0,
        description: l.description || undefined,
      })),
    }

    if (isEditing) {
      await updateEntry.mutateAsync({ id: entryId, ...input })
      navigate(`/accounting/journal-entries/${entryId}`)
    } else {
      const created = await createEntry.mutateAsync(input)
      navigate(`/accounting/journal-entries/${created.id}`)
    }
  }

  if (isEditing && isLoadingEntry) {
    return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('journal_entries.title'), href: '/accounting/journal-entries' },
            { label: '...' },
          ]}
        />
        <Card>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('journal_entries.title'), href: '/accounting/journal-entries' },
          {
            label: isEditing
              ? existingEntry?.entry_number ?? '...'
              : t('journal_entries.new_entry'),
            href: isEditing ? `/accounting/journal-entries/${entryId}` : undefined,
          },
          ...(isEditing ? [{ label: tc('actions.edit') }] : []),
        ]}
      />

      <PageHeader
        title={
          isEditing
            ? `${existingEntry?.entry_number ?? ''} — ${tc('actions.edit')}`
            : t('journal_entries.new_entry')
        }
        description={t('journal_entries.description')}
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Header */}
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label={t('labels.entry_date')}
              type="date"
              error={errors.entry_date?.message}
              {...register('entry_date')}
            />
            <Input
              label={t('labels.description')}
              placeholder={t('placeholders.description')}
              error={errors.description?.message}
              {...register('description')}
            />
            <Select
              label={t('labels.property')}
              options={propertyOptions}
              {...register('property_id')}
            />
          </div>
        </Card>

        {/* Lines */}
        <Card>
          <div className="space-y-3">
            <div className="overflow-x-auto">
              <table className="table table-sm w-full">
                <thead>
                  <tr>
                    <th className="w-1/3">{t('labels.account')}</th>
                    <th className="w-1/4">{t('labels.description')}</th>
                    <th className="w-1/6 text-right">{t('labels.debit')}</th>
                    <th className="w-1/6 text-right">{t('labels.credit')}</th>
                    <th className="w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index) => (
                    <tr key={field.id}>
                      <td>
                        <Select
                          options={glAccountOptions}
                          error={errors.journal_lines?.[index]?.gl_account_id?.message}
                          {...register(`journal_lines.${index}.gl_account_id`)}
                          size="sm"
                        />
                      </td>
                      <td>
                        <Input
                          type="text"
                          size="sm"
                          placeholder={t('placeholders.description')}
                          {...register(`journal_lines.${index}.description`)}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          size="sm"
                          inputClassName="text-right"
                          {...register(`journal_lines.${index}.debit`)}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          size="sm"
                          inputClassName="text-right"
                          {...register(`journal_lines.${index}.credit`)}
                        />
                      </td>
                      <td>
                        {fields.length > 2 && (
                          <Button
                            type="button"
                            size="xs"
                            variant="ghost"
                            onClick={() => remove(index)}
                            aria-label={t('journal_entries.remove_line')}
                          >
                            <Trash2 className="w-3 h-3 text-error" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold">
                    <td colSpan={2} className="text-right pr-2">
                      {tc('labels.total')}
                    </td>
                    <td className="text-right tabular-nums">
                      <span className={totalDebit > 0 ? '' : 'text-base-content/40'}>
                        {formatBaht(totalDebit, { locale: 'en', decimals: 2 })}
                      </span>
                    </td>
                    <td className="text-right tabular-nums">
                      <span className={totalCredit > 0 ? '' : 'text-base-content/40'}>
                        {formatBaht(totalCredit, { locale: 'en', decimals: 2 })}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                  <tr>
                    <td colSpan={5}>
                      <div className="flex items-center gap-2">
                        {isBalanced ? (
                          <span className="text-sm text-success font-medium">
                            ✓ {t('journal_entries.balance_ok')}
                          </span>
                        ) : (
                          <span className="text-sm text-error">
                            {t('journal_entries.balance_off')} (
                            {t('journal_entries.difference')}:{' '}
                            {formatBaht(Math.abs(totalDebit - totalCredit), { locale: 'en', decimals: 2 })})
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <Button
              type="button"
              size="sm"
              variant="ghost"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() =>
                append({ gl_account_id: '', debit: 0, credit: 0, description: '' })
              }
            >
              {t('journal_entries.add_line')}
            </Button>
          </div>
        </Card>

        {/* Actions */}
        <FormActions>
          <Button
            type="button"
            variant="ghost"
            onClick={() =>
              navigate(
                isEditing
                  ? `/accounting/journal-entries/${entryId}`
                  : '/accounting/journal-entries'
              )
            }
          >
            {tc('actions.cancel')}
          </Button>
          <Button
            type="submit"
            loading={isSubmitting || createEntry.isPending || updateEntry.isPending}
            disabled={!isBalanced}
          >
            {tc('actions.save')}
          </Button>
        </FormActions>
      </form>
    </div>
  )
}
